import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import PermissionResourceSchema from "permissions/resources/_schemas/permission-resource-schema";
import { LegalEntityAccessType } from "permissions/resources/legal-entities/legal-entity-access-types";
import { AccessLevel, ResourceType, Scope } from "permissions/resources/permission-resource-types";
import { z } from "zod";

export type LegalEntityAccessJSON = z.infer<typeof LegalEntityAccessSchema.LegalEntityAccessJSONZ>;

export type LegalEntityAccessParams = z.infer<typeof LegalEntityAccessSchema.LegalEntityAccessZ>;

export default class LegalEntityAccessSchema {
  public static readonly LegalEntityAccessJSONZ = z.object({
    ...PermissionResourceSchema.ResourceJSONZ.shape,
    resourceType: z.literal(ResourceType.LegalEntityAccess),
    scope: z.enum(Scope),
    accessLevel: z.enum(AccessLevel),
    accessType: z.enum(LegalEntityAccessType),
  });

  public static readonly LegalEntityAccessZ = z.object({
    ...PermissionResourceSchema.ResourceZ.shape,
    resourceType: z.literal(ResourceType.LegalEntityAccess),
    scope: z.enum(Scope),
    accessLevel: z.enum(AccessLevel),
    accessType: z.enum(LegalEntityAccessType),
  });

  public static validate(values: unknown): LegalEntityAccessParams {
    return parseOrThrow(LegalEntityAccessSchema.LegalEntityAccessZ, values, {
      logError: true,
      schemaName: "LegalEntityAccess",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(values: unknown): LegalEntityAccessJSON {
    return parseOrThrow(LegalEntityAccessSchema.LegalEntityAccessJSONZ, values, {
      logError: true,
      schemaName: "LegalEntityAccess",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValid(values: unknown): values is LegalEntityAccessParams {
    try {
      LegalEntityAccessSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(values: unknown): values is LegalEntityAccessJSON {
    try {
      LegalEntityAccessSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: LegalEntityAccessJSON): LegalEntityAccessParams {
    const parsed = parseOrThrow(LegalEntityAccessSchema.LegalEntityAccessJSONZ, data, {
      logError: true,
      schemaName: "LegalEntityAccess",
      operation: "deserialize",
      payloadSample: data,
    });
    return LegalEntityAccessSchema.codec.decode(parsed);
  }

  public static serialize(notification: LegalEntityAccessParams): LegalEntityAccessJSON {
    const parsed = LegalEntityAccessSchema.validate(notification);
    return LegalEntityAccessSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    LegalEntityAccessSchema.LegalEntityAccessJSONZ,
    LegalEntityAccessSchema.LegalEntityAccessZ,
    {
      decode: (data) => ({
        ...PermissionResourceSchema.deserialize(data),
        resourceType: ResourceType.LegalEntityAccess as const,
        accessType: data.accessType,
        accessLevel: data.accessLevel,
        scope: data.scope,
      }),
      encode: (resource) => ({
        ...PermissionResourceSchema.serialize(resource),
        resourceType: ResourceType.LegalEntityAccess as const,
        accessType: resource.accessType,
        accessLevel: resource.accessLevel,
        scope: resource.scope,
      }),
    },
  );
}
